import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import { CheckCircle, Download, FileArchive, FileText, Mail, Send, ShieldCheck, ShieldX } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Badge } from '@/shared/components/ui/Badge';
import { getApiErrorMessage } from '@/shared/api/axios';
import {
  downloadMonthZip, downloadPayslipPdf, fetchPayslipHtml,
  useApproveRun, useEmailPayslip, useFinalizePayslip, useGeneratePayslips, useOpenRun, usePayrollRuns, usePayslips, useRejectRun, useSubmitRun,
  type GenerateResult, type Payslip, type PayrollAccess,
} from './api';

const inr = (n: number | undefined) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const RUN_TONE = { draft: 'neutral', submitted: 'warning', rejected: 'danger', finalized: 'success' } as const;
const SOURCE_LABEL = { structure_engine: 'Salary structure', monthly_input: 'Typed lines', excel_upload: 'Spreadsheet' } as const;

/** The payslip in the company's payslip letter, fetched with the login token. */
function PayslipViewer({ payslip, onClose }: { payslip: Payslip | null; onClose: () => void }) {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    setHtml(null);
    setError('');
    if (!payslip) return;
    fetchPayslipHtml(payslip.id).then(setHtml).catch((err) => setError(getApiErrorMessage(err)));
  }, [payslip]);
  return (
    <Modal
      open={!!payslip}
      onClose={onClose}
      size="xl"
      title={
        <div className="flex w-full items-center justify-between pr-8">
          <span>Payslip: {payslip?.user.display_name} · {payslip?.month_year}</span>
          {payslip && (
            <Button size="sm" variant="primary" onClick={() => downloadPayslipPdf(payslip.id, `Payslip-${payslip.user.display_name}-${payslip.month_year}`).catch((e) => toast.error(getApiErrorMessage(e)))}>
              <Download className="mr-1 h-4 w-4" /> Download PDF
            </Button>
          )}
        </div>
      }
    >
      {error ? <p className="p-6 text-sm text-red-600">{error}</p> : html ? (
        <iframe title="Payslip" srcDoc={html} className="h-[70vh] w-full rounded border border-outline bg-white" />
      ) : <p className="p-6 text-sm text-on-surface-variant">Loading…</p>}
    </Modal>
  );
}

function GenerateResultDialog({ result, onClose }: { result: GenerateResult | null; onClose: () => void }) {
  return (
    <Modal open={!!result} onClose={onClose} title="Payslips generated" size="md" footer={<div className="flex justify-end"><Button onClick={onClose}>Done</Button></div>}>
      {result && (
        <div className="space-y-3 py-2 text-sm">
          <p>{result.message}</p>
          {result.blocked.length > 0 && (
            <div>
              <p className="mb-1 font-medium text-amber-700">Not generated ({result.blocked.length}):</p>
              <ul className="list-disc space-y-0.5 pl-5">
                {result.blocked.map((b) => <li key={b.userId}><strong>{b.name}</strong>: {b.reason}</li>)}
              </ul>
            </div>
          )}
          {result.notes.length > 0 && (
            <div>
              <p className="mb-1 font-medium">Worth knowing:</p>
              <ul className="list-disc space-y-0.5 pl-5 text-on-surface-variant">{result.notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

export function PayslipsTab({ access }: { access: PayrollAccess }) {
  const [month, setMonth] = useState(format(new Date(), 'yyyy-MM'));
  const { data: payslips, isLoading } = usePayslips(month);
  const { data: runs } = usePayrollRuns();
  const generate = useGeneratePayslips();
  const finalize = useFinalizePayslip();
  const email = useEmailPayslip();
  const openRun = useOpenRun();
  const submitRun = useSubmitRun();
  const approveRun = useApproveRun();
  const rejectRun = useRejectRun();
  const [viewing, setViewing] = useState<Payslip | null>(null);
  const [result, setResult] = useState<GenerateResult | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');

  const run = runs?.find((r) => r.month_year === month);
  const drafts = (payslips ?? []).filter((p) => p.status === 'draft');
  const fail = (err: unknown) => toast.error(getApiErrorMessage(err));

  const handleGenerate = async () => {
    if (!window.confirm(`Generate draft payslips for ${month}?`)) return;
    try {
      const res = await generate.mutateAsync(month);
      setResult(res);
    } catch (err) { fail(err); }
  };
  const handleSubmit = async () => {
    try {
      const opened = run ?? (await openRun.mutateAsync(month));
      const res = await submitRun.mutateAsync(opened.id);
      toast.success(res.message);
    } catch (err) { fail(err); }
  };

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" />
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => downloadMonthZip(month).catch(fail)} disabled={!payslips?.length}>
            <FileArchive className="mr-1 h-4 w-4" /> Download all (ZIP)
          </Button>
          {access.canManage && <Button onClick={handleGenerate} disabled={generate.isPending}>{generate.isPending ? 'Generating…' : 'Generate drafts'}</Button>}
        </div>
      </div>

      {(run || drafts.length > 0) && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-outline bg-surface-variant/40 p-3 text-sm">
          <div className="flex items-center gap-2">
            <span className="font-medium">Payroll run for {month}</span>
            <Badge tone={RUN_TONE[run?.status ?? 'draft']}>{(run?.status ?? 'draft').replace('_', ' ')}</Badge>
            {run?.status === 'submitted' && <span className="text-on-surface-variant">{run.payslip_count} payslips · net {inr(run.total_net)}</span>}
            {run?.status === 'rejected' && run.rejection_reason && <span className="text-red-600">Sent back: {run.rejection_reason}</span>}
          </div>
          <div className="flex gap-2">
            {access.canManage && drafts.length > 0 && (!run || run.status === 'draft' || run.status === 'rejected') && (
              <Button size="sm" onClick={handleSubmit} disabled={submitRun.isPending || openRun.isPending}><Send className="mr-1 h-4 w-4" /> Submit for approval</Button>
            )}
            {access.canFinalize && run?.status === 'submitted' && (
              <>
                <Button size="sm" onClick={() => approveRun.mutateAsync(run.id).then((r) => toast.success(r.message)).catch(fail)} disabled={approveRun.isPending}><ShieldCheck className="mr-1 h-4 w-4" /> Approve & finalize</Button>
                <Button size="sm" variant="outline" onClick={() => { setReason(''); setRejecting(true); }}><ShieldX className="mr-1 h-4 w-4" /> Send back</Button>
              </>
            )}
          </div>
        </div>
      )}

      <div className="max-h-[480px] overflow-y-auto">
        {isLoading ? <p className="p-8 text-center text-on-surface-variant">Loading payslips…</p> : !payslips?.length ? (
          <p className="p-8 text-center text-on-surface-variant">No payslips for {month}.{access.canManage ? ' Generate drafts, type the month\'s lines, or upload a sheet.' : ''}</p>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 z-10 bg-surface-variant text-xs uppercase text-on-surface-variant">
              <tr>
                <th className="px-4 py-3 font-medium">Employee</th>
                <th className="px-4 py-3 font-medium">Paid days</th>
                <th className="px-4 py-3 font-medium">Gross</th>
                <th className="px-4 py-3 font-medium">Deductions</th>
                <th className="px-4 py-3 font-medium">Net</th>
                <th className="px-4 py-3 font-medium">From</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline/50">
              {payslips.map((p) => (
                <tr key={p.id} className="hover:bg-surface-variant/40">
                  <td className="px-4 py-3"><div className="font-medium text-on-surface">{p.user.display_name}</div><div className="text-xs text-on-surface-variant">{p.user.position || p.user.department}</div></td>
                  <td className="px-4 py-3">{p.paid_days ?? '-'}{p.total_days ? ` / ${p.total_days}` : ''}</td>
                  <td className="px-4 py-3">{inr(p.earnings.reduce((s, e) => s + e.amount, 0))}</td>
                  <td className="px-4 py-3">{inr(p.deductions.reduce((s, d) => s + d.amount, 0))}</td>
                  <td className="px-4 py-3 font-semibold text-emerald-600">{inr(p.net_salary)}</td>
                  <td className="px-4 py-3 text-xs text-on-surface-variant">{SOURCE_LABEL[p.source]}</td>
                  <td className="px-4 py-3"><Badge tone={p.status === 'finalized' ? 'success' : 'warning'}>{p.status === 'finalized' ? 'Finalized' : 'Draft'}</Badge></td>
                  <td className="space-x-1 whitespace-nowrap px-4 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => setViewing(p)} title="Open the payslip"><FileText className="mr-1 h-4 w-4" />View</Button>
                    {access.canManage && (
                      <Button variant="ghost" size="sm" title="E-mail the payslip from your mailbox" disabled={email.isPending}
                        onClick={() => email.mutateAsync(p.id).then(() => toast.success('Payslip e-mailed.')).catch(fail)}><Mail className="h-4 w-4" /></Button>
                    )}
                    {access.canFinalize && p.status === 'draft' && (
                      <Button variant="ghost" size="sm" title="Finalize this payslip on its own" disabled={finalize.isPending}
                        onClick={() => finalize.mutateAsync(p.id).then(() => toast.success('Payslip finalized.')).catch(fail)}><CheckCircle className="h-4 w-4" /></Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <PayslipViewer payslip={viewing} onClose={() => setViewing(null)} />
      <GenerateResultDialog result={result} onClose={() => setResult(null)} />
      <Modal open={rejecting} onClose={() => setRejecting(false)} title="Send the run back" size="sm" footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setRejecting(false)}>Cancel</Button>
          <Button disabled={!reason.trim() || rejectRun.isPending} onClick={() => run && rejectRun.mutateAsync({ id: run.id, reason: reason.trim() }).then(() => { toast.success('Sent back to HR.'); setRejecting(false); }).catch(fail)}>Send back</Button>
        </div>
      }>
        <div className="space-y-2 py-2">
          <p className="text-sm text-on-surface-variant">Tell HR what to fix. The payslips stay as drafts.</p>
          <textarea className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" />
        </div>
      </Modal>
    </div>
  );
}
